import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';
import InviteAdminModal from './InviteAdminModal';
import { sendEmail } from '../../services/resendemailService';
import { inviteAdminTemplate } from '../../email_templates/inviteAdmin';
import TableWrapper from '../../components/ui/TableWrapper';
import StatusBadge from '../../components/ui/StatusBadge';

const { FiPlus, FiRefreshCw, FiXCircle, FiTrash2 } = FiIcons;

const AdminInvitesTab = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchInvites = async () => {
    setLoading(true);
    const res = await dataFetch('read/admin_invites');
    if (res?.status === 'success') {
      setInvites(res.data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const handleResend = async (invite) => {
    if (!window.confirm(`Resend invitation to ${invite.email}?`)) return;
    
    const expDate = new Date();
    expDate.setHours(expDate.getHours() + 72);
    
    const firstName = invite.email.split('@')[0];
    const sixDigitCode = Math.floor(100000 + Math.random() * 900000).toString();
    const tempPassword = invite.temp_password || `${firstName}@${sixDigitCode}!`;
    
    const res = await dataFetch(`update/admin_invites/${invite.id}`, {
      method: 'PUT',
      body: JSON.stringify({ 
        ...invite, 
        status: 'Pending', 
        expires_at: expDate.toISOString().slice(0, 19).replace('T', ' '), 
        temp_password: tempPassword 
      })
    });

    if (res?.status === 'success') {
      const inviteLink = `${window.location.origin}/login`;
      
      await sendEmail({
        to: invite.email,
        subject: "Invitation to Join Sentinel Admin Platform (Resent)",
        html: inviteAdminTemplate({
          name: firstName,
          email: invite.email,
          role: invite.role,
          tempPassword: tempPassword,
          loginUrl: inviteLink
        }),
        from: "noreply@sentinel.biscontractors.com"
      });

      fetchInvites();
    }
  };

  const handleRevoke = async (invite) => {
    if (!window.confirm(`Revoke invitation for ${invite.email}?`)) return;
    const res = await dataFetch(`update/admin_invites/${invite.id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...invite, status: 'Revoked' })
    });
    if (res?.status === 'success') fetchInvites();
  };

  const handleDelete = async (invite) => {
    if (!window.confirm(`Delete invitation record for ${invite.email}?`)) return;
    const res = await dataFetch(`delete/admin_invites/${invite.id}`, { method: 'DELETE' });
    if (res?.status === 'success') fetchInvites();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Pending & Past Invitations</h3>
          <p className="text-sm text-slate-500">Manage invitations sent to administrators and managers.</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
        >
          <SafeIcon icon={FiPlus} />
          Invite Users
        </button>
      </div>

      <TableWrapper>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
              <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
              <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Expires At</th>
              <th className="p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan="5" className="p-8 text-center text-slate-500">Loading...</td></tr>
            ) : invites.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-slate-500">No invitations found.</td></tr>
            ) : (
              invites.map(inv => {
                const isExpired = inv.status === 'Pending' && new Date(inv.expires_at) < new Date();
                const displayStatus = isExpired ? 'Expired' : inv.status;
                
                return (
                  <tr key={inv.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 text-sm font-medium text-slate-800">{inv.email}</td>
                    <td className="p-4 text-sm text-slate-600">{inv.role}</td>
                    <td className="p-4">
                      <StatusBadge status={displayStatus} />
                    </td>
                    <td className="p-4 text-sm text-slate-500">
                      {new Date(inv.expires_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right space-x-2">
                      {(displayStatus === 'Pending' || displayStatus === 'Expired') && (
                        <>
                          <button onClick={() => handleResend(inv)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Resend">
                            <SafeIcon icon={FiRefreshCw} />
                          </button>
                          <button onClick={() => handleRevoke(inv)} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors" title="Revoke">
                            <SafeIcon icon={FiXCircle} />
                          </button>
                        </>
                      )}
                      <button onClick={() => handleDelete(inv)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete">
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </TableWrapper>

      {showModal && (
        <InviteAdminModal 
          onClose={() => setShowModal(false)} 
          onSuccess={() => { setShowModal(false); fetchInvites(); }} 
        />
      )}
    </div>
  );
};

export default AdminInvitesTab;